/**
 * ตรรกะการข้ามฉาก — อยู่ที่ **มือถือ** (MQTT_PROTOCOL.md §5, PHYSICS.md §9)
 *
 * NB รู้แค่ว่าตัวละครถึงขอบ แต่ห้ามคุยกับ NB อื่นเอง ต้องผ่านมือถือทุกครั้ง
 * เพื่อให้มีที่เดียวที่รู้ภาพรวมทั้ง 6 ฐาน — debug ง่ายกว่ามาก
 *
 * แยกเป็นฟังก์ชันล้วนเพื่อให้เทสต์ T1 (ข้ามไปกลับ 20 รอบ ไม่มีตัวค้างสองจอ)
 * รันได้โดยไม่ต้องมี broker
 */
import {
  GROUND_Y, SCENE_W, SCENES, STYLE_BY_SCENE, type SceneId,
  type StyleId, WORLD_CLAMP_LEFT, WORLD_CLAMP_RIGHT,
} from "@py/protocol";

/**
 * ส่งมอบให้ฉากถัดไปตอนตัวละคร**ยังอยู่บนพื้น** ไม่ใช่รอให้เดินพ้น x=1080
 *
 * ผังชนของฉาก 1–3 กว้าง 18 ช่อง = 1080 px พอดี เดินพ้นขอบไปแล้วไม่มีพื้นรองรับ
 * ตัวละครจะเริ่มร่วงทันที ค่านี้ตรงกับจุดที่จอยิง `edge` (x < 4 หรือ x > SCENE_W - 4)
 * ซึ่งส่งที่ QoS 1 ทุกเฟรม จึงรู้เรื่องทันทีไม่ต้องรอ `pos` ที่มาแค่ 10 Hz
 *
 * ใช้ค่าเดียวกันทั้งสองฝั่ง — เดิมฝั่งซ้ายรอถึง x ≤ -40 ซึ่งเลยขอบผังไปแล้ว
 * ตัวละครจึงร่วงลงเหวก่อนจะได้ข้าม (อาการ "กลับฐานก่อนหน้าไม่ได้")
 */
const CROSS_ENTER = 4;

/**
 * ตำแหน่งที่ไปโผล่ในฉากใหม่ — วัดจากขอบด้านที่เดินเข้ามา
 *
 * ไม่ใช้พิกัดต่อเนื่อง (x - SCENE_W ≈ -4) ด้วยสองเหตุผล:
 *  1. ค่านั้นอยู่**นอกผังชน** ตัวชนครึ่งหนึ่งจึงลอยอยู่นอกแผนที่ ฉากที่เป็น
 *     platformer จะไม่มีพื้นรองรับแล้วร่วงทันทีที่รับ authority
 *  2. เป็น hysteresis ในตัว — โผล่ห่างขอบ 90 px ขณะที่เกณฑ์ข้ามอยู่ที่ 4 px
 *     จึงไม่มีทางเข้าเงื่อนไข "ข้ามกลับ" ทันทีแล้วเด้งไปมาที่รอยต่อ
 */
const ENTER_INSET = 90;

export type CrossingState = {
  /** ฉากที่รันฟิสิกส์อยู่ (authority) — มีได้ทีละฉากเดียวเสมอ */
  authScene: SceneId;
  x: number;
  y: number;
};

export type CrossingAction =
  | { kind: "spawn"; scene: SceneId; x: number; y: number; style: StyleId; auth: boolean; warp: boolean }
  | { kind: "release"; scene: SceneId }
  | { kind: "switchInput"; scene: SceneId };

export function newCrossing(scene: SceneId, x: number, y: number): CrossingState {
  return { authScene: scene, x, y };
}

const clampScene = (n: number): SceneId =>
  Math.min(SCENES - 1, Math.max(0, n)) as SceneId;

/**
 * เดินสถานะหนึ่งก้าวจากพิกัดที่ NB รายงานกลับมา
 * คืนรายการคำสั่งที่มือถือต้อง publish ตามลำดับ
 *
 * **ตัวละครอยู่ทีละจอเดียวเสมอ** — เดิมมีโซนคาบเกี่ยว 170 px ที่วาด ghost
 * บนจอข้างเคียงไปด้วย ผลคือผู้เล่นเห็นตัวเองสองจอพร้อมกันตรงรอยต่อ
 * และรู้สึกเหมือนกำลังบังคับสองฉากพร้อมกัน · ตอนนี้ถึงขอบแล้วส่งมอบทันที
 * ให้เดินทะลุไปเลยเหมือนเปลี่ยนห้อง (ดู DECISIONS.md D-24)
 */
export function stepCrossing(
  st: CrossingState,
  pos: { s: SceneId; x: number; y: number },
): CrossingAction[] {
  if (pos.s !== st.authScene) return []; // รายงานจากจอที่ไม่ได้ถือ authority — ทิ้ง

  st.x = pos.x;
  st.y = pos.y;

  const crossRight = pos.x >= SCENE_W - CROSS_ENTER && st.authScene < SCENES - 1;
  const crossLeft = pos.x <= CROSS_ENTER && st.authScene > 0;
  if (!crossRight && !crossLeft) return [];

  const from = st.authScene;
  const to = clampScene(from + (crossRight ? 1 : -1));
  // เข้าฉากใหม่จากฝั่งตรงข้ามกับที่เดินออก
  const nx = crossRight ? ENTER_INSET : SCENE_W - ENTER_INSET;
  // ห้าม spawn ต่ำกว่าเส้นพื้นร่วม — ถ้าตัวละครกำลังร่วงตอนส่งมอบ
  // ฉากใหม่จะรับไปด้วย y ที่อยู่ใต้พื้นแล้วร่วงต่อไม่หยุด
  const ny = Math.min(pos.y, GROUND_Y);

  st.authScene = to;
  st.x = nx;
  st.y = ny;

  return [
    { kind: "spawn", scene: to, x: nx, y: ny,
      style: STYLE_BY_SCENE[to], auth: true, warp: false },
    { kind: "release", scene: from },
    { kind: "switchInput", scene: to },
  ];
}

/** ฉากแรกซ้ายสุดและฉากท้ายขวาสุดต้องกันตัวละครหลุดโลก */
export function clampWorldX(scene: SceneId, x: number): number {
  if (scene === 0) return Math.max(WORLD_CLAMP_LEFT, x);
  if (scene === SCENES - 1) return Math.min(WORLD_CLAMP_RIGHT, x);
  return x;
}
